import React, { useEffect, useState } from "react";
import api from "../../../axiosConfig";
import { Container, Table, Button, Form, Modal, Spinner, Card, InputGroup, Row, Col } from "react-bootstrap";
import { toast } from "react-toastify";

export default function UserListPage() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  // Create Modal State
  const [showCreate, setShowCreate] = useState(false);
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPassword2, setNewPassword2] = useState("");
  const [creating, setCreating] = useState(false);

  // Edit Modal State
  const [showEdit, setShowEdit] = useState(false);
  const [editUser, setEditUser] = useState(null); // { id, username, is_active, role_ids, overrides }
  const [savingEdit, setSavingEdit] = useState(false);

  // Change Password Modal State
  const [showPassword, setShowPassword] = useState(false);
  const [passwordUserId, setPasswordUserId] = useState(null);
  const [passwordUsername, setPasswordUsername] = useState("");
  const [changePassword, setChangePassword] = useState("");
  const [changePassword2, setChangePassword2] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await api.get("/auth/admin/users", {
        params: { q, skip: 0, limit: 100 },
      });
      setUsers(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la încărcarea utilizatorilor");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [q]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newUsername.trim()) return toast.warning("Introduceți username-ul.");
    if (!newPassword) return toast.warning("Introduceți parola.");
    if (newPassword.length < 6) return toast.warning("Parola trebuie să aibă minim 6 caractere.");
    if (newPassword !== newPassword2) return toast.warning("Parolele nu coincid.");

    setCreating(true);
    try {
      await api.post("/auth/add-user", {
        username: newUsername.trim(),
        password: newPassword,
        is_active: true,
      });
      toast.success("Utilizator adăugat cu succes!");
      setShowCreate(false);
      setNewUsername("");
      setNewPassword("");
      setNewPassword2("");
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eroare la adăugarea utilizatorului.");
    } finally {
      setCreating(false);
    }
  };

  const openEdit = async (user) => {
    try {
      // Fetch full details (roles and overrides) to not lose them during PUT
      const res = await api.get(`/auth/admin/users/${user.id}`);
      setEditUser({
        id: res.data.id,
        username: res.data.username,
        is_active: res.data.is_active,
        role_ids: res.data.role_ids || [],
        overrides: res.data.overrides || [],
      });
      setShowEdit(true);
    } catch (e) {
      toast.error("Eroare la încărcarea detaliilor utilizatorului.");
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    try {
      await api.put(`/auth/admin/users/${editUser.id}`, {
        is_active: editUser.is_active,
        role_ids: editUser.role_ids,
        overrides: editUser.overrides,
      });
      toast.success("Utilizator actualizat cu succes!");
      setShowEdit(false);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eroare la salvarea modificărilor.");
    } finally {
      setSavingEdit(false);
    }
  };

  const openChangePassword = (user) => {
    setPasswordUserId(user.id);
    setPasswordUsername(user.username);
    setChangePassword("");
    setChangePassword2("");
    setShowPassword(true);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!changePassword) return toast.warning("Introduceți parola nouă.");
    if (changePassword.length < 6) return toast.warning("Parola trebuie să aibă minim 6 caractere.");
    if (changePassword !== changePassword2) return toast.warning("Parolele nu coincid.");

    setSavingPassword(true);
    try {
      await api.put(`/auth/admin/users/${passwordUserId}/password`, {
        password: changePassword,
      });
      toast.success("Parola a fost modificată cu succes!");
      setShowPassword(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eroare la modificarea parolei.");
    } finally {
      setSavingPassword(false);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Sigur doriți să ștergeți utilizatorul "${user.username}"?`)) return;
    try {
      await api.delete(`/auth/admin/users/${user.id}`);
      toast.success("Utilizator șters cu succes!");
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eroare la ștergerea utilizatorului.");
    }
  };

  return (
    <Container className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Administrare Utilizatori</h2>
        <Button variant="primary" onClick={() => setShowCreate(true)}>
          + Adaugă Utilizator
        </Button>
      </div>

      <Card className="mb-4 shadow-sm border-0 bg-light p-3">
        <Row className="align-items-center">
          <Col md={6}>
            <InputGroup>
              <Form.Control
                placeholder="Caută după username..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
              <Button variant="outline-secondary" onClick={() => setQ("")}>
                Reset
              </Button>
            </InputGroup>
          </Col>
          <Col md={6} className="text-md-end mt-2 mt-md-0 text-muted">
            Total utilizatori: {total}
          </Col>
        </Row>
      </Card>

      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-0">
            <Table responsive hover className="mb-0">
              <thead className="bg-dark text-white">
                <tr>
                  <th>Username</th>
                  <th>Stare Cont</th>
                  <th className="text-end">Acțiuni</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="text-center py-4 text-muted">
                      Nu s-au găsit utilizatori.
                    </td>
                  </tr>
                ) : (
                  users.map((u) => (
                    <tr key={u.id}>
                      <td className="align-middle fw-semibold">{u.username}</td>
                      <td className="align-middle">
                        {u.is_active ? (
                          <span className="badge bg-success">Activ</span>
                        ) : (
                          <span className="badge bg-secondary">Inactiv</span>
                        )}
                      </td>
                      <td className="align-middle text-end">
                        <Button
                          variant="info"
                          size="sm"
                          className="me-2 text-white"
                          onClick={() => openEdit(u)}
                        >
                          Editează Stare
                        </Button>
                        <Button
                          variant="warning"
                          size="sm"
                          className="me-2 text-dark"
                          onClick={() => openChangePassword(u)}
                        >
                          Schimbă Parola
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDelete(u)}
                        >
                          Șterge
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {/* Create Modal */}
      <Modal show={showCreate} onHide={() => setShowCreate(false)}>
        <Form onSubmit={handleCreate}>
          <Modal.Header closeButton>
            <Modal.Title>Adaugă Utilizator Nou</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Username</Form.Label>
              <Form.Control
                type="text"
                required
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                placeholder="Ex: ionpopescu"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Parolă</Form.Label>
              <Form.Control
                type="password"
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minim 6 caractere"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Confirmă Parolă</Form.Label>
              <Form.Control
                type="password"
                required
                value={newPassword2}
                onChange={(e) => setNewPassword2(e.target.value)}
                placeholder="Reintroduceți parola"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowCreate(false)}>
              Renunță
            </Button>
            <Button variant="primary" type="submit" disabled={creating}>
              {creating ? <Spinner size="sm" /> : "Înregistrează"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Edit Modal */}
      <Modal show={showEdit} onHide={() => setShowEdit(false)}>
        <Form onSubmit={handleEdit}>
          <Modal.Header closeButton>
            <Modal.Title>Editează Stare Utilizator: {editUser?.username}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {editUser && (
              <Form.Check
                type="switch"
                id="edit-is-active"
                label="Utilizator Activ (permite login)"
                checked={editUser.is_active}
                onChange={(e) => setEditUser({ ...editUser, is_active: e.target.checked })}
              />
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowEdit(false)}>
              Renunță
            </Button>
            <Button variant="primary" type="submit" disabled={savingEdit}>
              {savingEdit ? <Spinner size="sm" /> : "Salvează"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Change Password Modal */}
      <Modal show={showPassword} onHide={() => setShowPassword(false)}>
        <Form onSubmit={handleChangePassword}>
          <Modal.Header closeButton>
            <Modal.Title>Schimbă Parola pentru: {passwordUsername}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Parolă Nouă</Form.Label>
              <Form.Control
                type="password"
                required
                value={changePassword}
                onChange={(e) => setChangePassword(e.target.value)}
                placeholder="Minim 6 caractere"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Confirmă Parola Nouă</Form.Label>
              <Form.Control
                type="password"
                required
                value={changePassword2}
                onChange={(e) => setChangePassword2(e.target.value)}
                placeholder="Reintroduceți parola"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowPassword(false)}>
              Renunță
            </Button>
            <Button variant="primary" type="submit" disabled={savingPassword}>
              {savingPassword ? <Spinner size="sm" /> : "Actualizează Parola"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
}
